import Layout from "./Layout.jsx";

import Dashboard from "./Dashboard";
import LandingPage from "./LandingPage";
import Home from "./Home";
import Sobre from "./Sobre";
import Modulos from "./Modulos";
import Contato from "./Contato";
import Seguranca from "./Seguranca";
import Privacidade from "./Privacidade";

import Propriedades from "./Propriedades";

import Financeiro from "./Financeiro";

import Safras from "./Safras";
import SafraDetalhes from "./SafraDetalhes";
import Talhoes from "./Talhoes";
import Animais from "./Animais";
import ProducaoLeite from "./ProducaoLeite";

import Estoque from "./Estoque";

import Custos from "./Custos";

import Relatorios from "./Relatorios";
import LCDPR from "./LCDPR";

import Simulador from "./Simulador";

import ContasPagar from "./ContasPagar";

import ContasReceber from "./ContasReceber";

import Orcamento from "./Orcamento";

import Ativos from "./Ativos";

import Financiamentos from "./Financiamentos";

import ArmazensGraos from "./ArmazensGraos";
import MovimentacaoGraoNova from "./MovimentacaoGraoNova";
import Parceiros from "./Parceiros";

import { useLicensePayment } from "@/hooks/useLicensePayment";
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { PropriedadeProvider } from "@/components/shared/PropriedadeProvider";
import Login from "./Login.jsx";
import PerfilProdutor from "./PerfilProdutor.jsx";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { agrogestao } from "@/api/agrogestaoClient.js";
import Admin from "./Admin";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Create a wrapper component that uses useLocation inside the Router context
function PagesContent() {
  const location = useLocation();
  const action = new URLSearchParams(location.search).get('action');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Payment Hook
  const { pixLoading, pixError, pixData, gerarPix, verificarPagamento } = useLicensePayment();
  const [showPixUi, setShowPixUi] = useState(action === 'pix');

  // Payment Form State
  const [valor, setValor] = useState("200,00");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerTaxId, setCustomerTaxId] = useState("");

  const [selectedPropId, setSelectedPropId] = useState(localStorage.getItem("propriedade_selecionada") || "");
  const [propsList, setPropsList] = useState([]);
  const [profileComplete, setProfileComplete] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const SESSION_MAX_IDLE_MS = 30 * 60 * 1000;

  useEffect(() => {
    const run = async () => {
      try {
        const u = await agrogestao.auth.me();
        if (u) {
          await agrogestao.access.logAudit({ acao: 'navigate', details: { route: location.pathname } });
        }
      } catch { }
    };
    run();
  }, [location.pathname]);

  useEffect(() => {
    let timer = null;
    const reset = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        try {
          // Only sign out if user is logged in
          const { data } = await agrogestao.client.auth.getSession();
          if (data?.session) {
            await agrogestao.auth.signOut();
            window.location.href = '/?action=signin';
          }
        } catch { }
      }, SESSION_MAX_IDLE_MS);
    };
    ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'].forEach(evt => {
      window.addEventListener(evt, reset);
    });
    reset();
    return () => {
      ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'].forEach(evt => {
        window.removeEventListener(evt, reset);
      });
      if (timer) clearTimeout(timer);
    };
  }, []);

  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  const { data: lic = [], isLoading: loadingLic } = useQuery({
    queryKey: ['licenca', propriedadeId],
    queryFn: async () => {
      if (!propriedadeId) return [];
      try {
        const data = await agrogestao.entities.Licenca.filter({ propriedade_id: propriedadeId }, '-valid_until', 1);
        return data || [];
      } catch (err) {
        console.warn("Licenca query failed:", err.message);
        return [];
      }
    },
    enabled: !!propriedadeId
  });
  const licenca = lic && lic.length > 0 ? lic[0] : null;
  const licOk = !!licenca && (licenca.status !== 'bloqueado' && (!licenca.valid_until || new Date(licenca.valid_until) >= new Date()));

  const { data: propriedadeSel } = useQuery({
    queryKey: ['propriedade_info', propriedadeId],
    queryFn: async () => {
      if (!propriedadeId) return null;
      try {
        const r = await agrogestao.entities.Propriedade.filter({ id: propriedadeId });
        return r?.[0] || null;
      } catch (err) {
        console.warn("Propriedade query failed:", err.message);
        return null;
      }
    },
    enabled: !!propriedadeId
  });

  useEffect(() => {
    let unsub = null;
    const init = async () => {
      try {
        const u = await agrogestao.auth.me();
        setUser(u);

        if (u?.email) {
          // Check Profile and Admin Role from Database
          const { data } = await agrogestao.client.from('users').select('*').ilike('email', u.email).maybeSingle();

          if (data) {
            const digits = (s) => String(s || '').replace(/\D/g, '');
            let ok = false;
            const hasPhone = digits(data.telefone).length > 0 || digits(data.whatsapp).length > 0;
            const cepOk = digits(data.endereco_cep).length === 8;
            const ufOk = String(data.endereco_estado || '').trim().length === 2;
            const cidadeOk = !!data.endereco_cidade;
            if ((data.tipo_pessoa || 'pf') === 'pf') {
              ok = !!data.full_name && digits(data.cpf).length === 11 && hasPhone && cidadeOk && ufOk && cepOk;
            } else {
              ok = digits(data.cnpj).length === 14 && (!!data.razao_social || !!data.full_name) && hasPhone && cidadeOk && ufOk && cepOk;
            }
            setProfileComplete(ok);

            // SECURE ADMIN CHECK: Role based only
            const admin = data?.role === 'admin';
            setIsAdminUser(!!admin);
          } else {
            setProfileComplete(null);
            setIsAdminUser(false);
          }
        } else {
          setProfileComplete(null);
          setIsAdminUser(false);
        }
      } catch {
        setProfileComplete(null);
        setIsAdminUser(false);
      } finally {
        setLoading(false);
      }
    };
    init();
    unsub = agrogestao.client.auth.onAuthStateChange((event, session) => {
      const u = session?.user || null;
      setUser(u);
      try {
        if (event === 'SIGNED_IN' && u) {
          agrogestao.access.logAudit({ acao: 'login', status: 'success', details: { route: location.pathname } });
        } else if (event === 'SIGNED_OUT') {
          // Skip logging logout if session is already gone to avoid 401
          console.log("Sessão encerrada");
        }
      } catch { }
    });
    return () => {
      if (unsub && typeof unsub.subscription?.unsubscribe === "function") {
        unsub.subscription.unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    const loadProps = async () => {
      if (!propriedadeId) {
        try {
          const { data: sData } = await agrogestao.client.auth.getSession();
          const userEmail = sData?.session?.user?.email || '';
          if (!userEmail) { setPropsList([]); return; }
          const { data, error } = await agrogestao.client
            .from('propriedades')
            .select('id, nome, created_by')
            .eq('created_by', userEmail)
            .order('nome', { ascending: true })
            .limit(1000);
          if (error) { setPropsList([]); return; }
          setPropsList(Array.isArray(data) ? data : []);
        } catch { setPropsList([]); }
      }
    };
    loadProps();
  }, [propriedadeId]);

  useEffect(() => {
    const pickDefaultProp = async () => {
      try {
        if (propriedadeId) return;
        if (!user) return;
        const key = user.email ? `default_propriedade_${user.email}` : 'default_propriedade';
        let targetId = '';
        try { targetId = localStorage.getItem(key) || ''; } catch { }
        if (!targetId && propsList.length >= 1) {
          targetId = String(propsList[0]?.id || '');
        }
        if (!targetId) return;
        try { localStorage.setItem('propriedade_selecionada', targetId); } catch { }
        try { localStorage.setItem(key, targetId); } catch { }
      } catch { }
    };
    pickDefaultProp();
  }, [user, propsList, propriedadeId]);

  const handleGerarPix = async () => {
    try {
      const pid = selectedPropId || propriedadeId;
      if (!pid) return;

      let amount = 20000;
      const propInfo = propsList.find(p => String(p.id) === String(pid)) || propriedadeSel;
      if (propInfo && Number(propInfo.valor_licenca_mensal || 0) > 0) {
        amount = Number(propInfo.valor_licenca_mensal);
      }

      await gerarPix({
        propriedadeId: pid,
        valor: amount,
        customer: {
          name: customerName,
          email: customerEmail,
          phone: customerPhone,
          taxId: customerTaxId
        }
      });
    } catch (e) {
      // Error handled in hook 
    }
  };

  const copyBrCode = async () => {
    if (pixData?.brCode) {
      try { await navigator.clipboard.writeText(pixData.brCode); } catch { }
    }
  };

  const handleVerificarPagamento = async () => {
    const pid = selectedPropId || propriedadeId;
    const ok = await verificarPagamento(pid);
    if (ok) {
      window.location.reload();
    }
  };

  if (loading || loadingLic) {
    return <div className="min-h-screen flex items-center justify-center">Carregando...</div>;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/sobre" element={<Sobre />} />
        <Route path="/modulos" element={<Modulos />} />
        <Route path="/contato" element={<Contato />} />
        <Route path="/seguranca" element={<Seguranca />} />
        <Route path="/privacidade" element={<Privacidade />} />
        <Route path="*" element={<Login mode={action === 'reset' ? 'reset' : 'signin'} />} />
      </Routes>
    )
  }

  if (propriedadeId && propriedadeSel && !licOk && location.pathname !== '/Propriedades') {
    return (
      <Layout currentPageName={"Admin"}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="max-w-2xl w-full p-6 bg-white border border-red-200 rounded-xl">
            <div className="text-center">
              <div className="text-red-700 font-bold text-lg mb-2">Licença bloqueada ou vencida</div>
              <p className="text-stone-700 mb-4">A propriedade selecionada está sem licença ativa. Deseja gerar o pagamento para desbloquear?</p>
            </div>
            <div className="flex gap-2 justify-center mb-4">
              <a href="/Propriedades" className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg">Trocar Propriedade</a>
              {!showPixUi && (
                <Button onClick={() => setShowPixUi(true)} className="bg-emerald-600 hover:bg-emerald-700">Gerar pagamento PIX</Button>
              )}
            </div>
            {showPixUi && (
              <Card>
                <CardContent className="p-4 space-y-4">
                  <div className="font-medium">Gerar pagamento PIX para desbloquear</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Valor (R$)</Label>
                      <Input value={valor} disabled />
                    </div>
                    <div className="space-y-1">
                      <Label>Nome *</Label>
                      <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} required />
                    </div>
                    <div className="space-y-1">
                      <Label>E-mail *</Label>
                      <Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} required />
                    </div>
                    <div className="space-y-1">
                      <Label>Celular *</Label>
                      <Input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} required />
                    </div>
                    <div className="space-y-1">
                      <Label>CPF/CNPJ *</Label>
                      <Input value={customerTaxId} onChange={(e) => setCustomerTaxId(e.target.value)} required />
                    </div>
                  </div>
                  {pixError && <div className="text-red-600 text-sm">{pixError}</div>}
                  <div className="flex gap-2">
                    <Button onClick={handleGerarPix} disabled={pixLoading} className="bg-emerald-600 hover:bg-emerald-700">
                      {pixLoading ? 'Gerando...' : 'Gerar QRCode PIX'}
                    </Button>
                  </div>
                  {pixData && (
                    <div className="mt-4 space-y-3">
                      {pixData.brCodeBase64 && <img src={pixData.brCodeBase64} alt="QRCode Pix" className="mx-auto w-60 h-60 object-contain" />}
                      {pixData.brCode && (
                        <div className="text-sm break-all bg-stone-100 p-2 rounded">
                          {pixData.brCode}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={copyBrCode}>Copiar código copia-e-cola</Button>
                        <Button onClick={handleVerificarPagamento} className="bg-indigo-600 hover:bg-indigo-700">Verificar pagamento</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  if (isAdminUser) {
    return (
      <Layout currentPageName={'Admin'}>
        <Admin />
      </Layout>
    );
  }

  if (!propriedadeId) {
    return (
      <Layout currentPageName={'Propriedades'}>
        <Propriedades />
      </Layout>
    )
  }

  if (profileComplete === false) {
    return (
      <Layout currentPageName={'PerfilProdutor'}>
        <PerfilProdutor />
      </Layout>
    )
  }

  // Default Route Handler
  const currentPageName = location.pathname.slice(1) || 'Dashboard';

  return (
    <Layout currentPageName={currentPageName}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/Dashboard" element={<Dashboard />} />
        <Route path="/Propriedades" element={<Propriedades />} />
        <Route path="/Financeiro" element={<Financeiro />} />
        <Route path="/Safras" element={<Safras />} />
        <Route path="/Safras/:id" element={<SafraDetalhes />} />
        <Route path="/Talhoes" element={<Talhoes />} />
        <Route path="/Animais" element={<Animais />} />
        <Route path="/ProducaoLeite" element={<ProducaoLeite />} />
        <Route path="/Estoque" element={<Estoque />} />
        <Route path="/Custos" element={<Custos />} />
        <Route path="/Relatorios" element={<Relatorios />} />
        <Route path="/LCDPR" element={<LCDPR />} />
        <Route path="/Simulador" element={<Simulador />} />
        <Route path="/ContasPagar" element={<ContasPagar />} />
        <Route path="/ContasReceber" element={<ContasReceber />} />
        <Route path="/Orcamento" element={<Orcamento />} />
        <Route path="/Ativos" element={<Ativos />} />
        <Route path="/Financiamentos" element={<Financiamentos />} />
        <Route path="/ArmazensGraos" element={<ArmazensGraos />} />
        <Route path="/MovimentacaoGrao" element={<MovimentacaoGraoNova />} />
        <Route path="/Parceiros" element={<Parceiros />} />
        <Route path="/PerfilProdutor" element={<PerfilProdutor />} />
        <Route path="/Admin" element={isAdminUser ? <Admin /> : <Login mode={'signin'} />} />
      </Routes>
    </Layout>
  );
}

export default function Pages() {
  return (
    <PropriedadeProvider>
      <Router>
        <PagesContent />
      </Router>
    </PropriedadeProvider>
  );
}
